import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store, select } from '@ngrx/store';
import { LazyScrollableDataSource } from '@rappi/common';
import { SelectConfig } from '@rappi/ui/select';
import { Observable, Subject, of } from 'rxjs';
import { catchError, finalize, map, take, takeUntil } from 'rxjs/operators';

import {
  MONTHS,
  PERIOD_MIN,
  YEARS,
  LANGUAGES
} from '../../shared/constants';
import { STORAGE_KEY } from '../../../../definitions/app.constants';

import { NewReportForm, ReportTypeDto, SegmentationAxis, CreateReportRequestBody } from '../../shared/interfaces';
import { AppState } from '../../../../store/states/app.state';
import { StoreName } from '../../../../store/definitions/store.constants';
import { CountryState } from '../../../../store/states/country.state';
import { COUNTRY_NAME } from '../../../../definitions/countries.constants';
import { ReportAutomationService } from '../../services/report-automation.service';
import { AlertsService } from '../../../../services/alerts/alerts.service';
import { AlertsType } from '@rappi/ui/alerts';
import { Entity, Paginate, Permission } from '../../../../permissions/definitions/permissions.models';
import { MasterDataEntity } from '../../../../shared/enums';
import { FilterParams, FilterRequest } from '../../../../permissions/definitions/filters.models';
import { SearchCriteria } from '../../../../permissions/definitions/permissions.enums';
import { FiltersService } from '../../../../permissions/services/filters.service';

const LANGUAGE_CODE: Record<string, string> = { Español: 'es', English: 'en', Português: 'pt' };
const CAMPAIGN_REPORT_CODE = 'rpc_campaign_report';

const AXIS_LIST: { key: SegmentationAxis; label: string }[] = [
  { key: 'category', label: 'Category' },
  { key: 'subcategory', label: 'Subcategory' },
  { key: 'segment', label: 'Segment' }
];

@Component({
  selector: 'app-new-report-modal',
  templateUrl: './new-report-modal.component.html',
  styleUrls: ['./new-report-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NewReportModalComponent implements OnInit, OnDestroy {
  step = 1;

  form: NewReportForm = {
    type: '', country: '', maker: '', month: '', year: '', language: '',
    trademark: '', retailer: '', vertical: '', axis: '', axisCategory: '', axisValue: '', period: '',
    startMonth: '', startYear: '', campaignTag: ''
  };

  reportTypes: ReportTypeDto[] = [];
  loadingReportTypes = false;
  readonly months = MONTHS;
  readonly years = YEARS;
  readonly languages = LANGUAGES;
  readonly periodMin = PERIOD_MIN;
  readonly axisList = AXIS_LIST;

  readonly makerSelectConfig: SelectConfig = { panelClass: 'one-select', search: true, includeAll: false };
  readonly retailerSelectConfig: SelectConfig = { panelClass: 'one-select', search: true, includeAll: false };
  selectedMaker: Entity | null = null;
  selectedRetailer: Entity | null = null;
  submitting = false;
  makersDataSource: LazyScrollableDataSource<Entity>;
  retailersDataSource: LazyScrollableDataSource<Entity>;

  trademarkOptions: string[] = [];
  verticalOptions: string[] = [];
  categoryOptions: string[] = [];
  subcategoryOptions: string[] = [];
  segmentOptions: string[] = [];

  loadingTrademarks = false;
  loadingVerticals = false;
  loadingCategories = false;
  loadingSubcategories = false;
  loadingSegments = false;

  private _countryCode = '';
  private _trademarkEntities: Entity[] = [];
  private _verticalEntities: Entity[] = [];
  private _categoryEntities: Entity[] = [];
  private _subcategoryEntities: Entity[] = [];
  private _segmentEntities: Entity[] = [];
  private readonly DESTROY_SUBJECT$ = new Subject<void>();

  constructor(
    public readonly dialogRef: MatDialogRef<NewReportModalComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: unknown,
    private readonly _store: Store<AppState>,
    private readonly _raService: ReportAutomationService,
    private readonly _filtersService: FiltersService,
    private readonly _alerts: AlertsService
  ) {
    this._newMakersDataSource();
    this._newRetailersDataSource();
  }

  ngOnInit(): void {
    this._store.pipe(select(StoreName.country), take(1)).subscribe((state: CountryState) => {
      this._countryCode = state.countrySelected;
      const name = COUNTRY_NAME[state.countrySelected];
      if (name) {
 this.form.country = name;
}
    });
    this._loadReportTypes();
    this._loadVerticals();
  }

  ngOnDestroy(): void {
    this.DESTROY_SUBJECT$.next();
    this.DESTROY_SUBJECT$.complete();
  }

  get selectedType(): ReportTypeDto | undefined {
    return this.reportTypes.find(t => t.name === this.form.type);
  }

  get isCampaignReport(): boolean {
    return this.selectedType?.code === CAMPAIGN_REPORT_CODE;
  }

  get typeHasFilters(): boolean {
    return !!this.selectedType && !this.isCampaignReport;
  }

  get stepTotal(): number {
    return this.typeHasFilters ? 3 : 2;
  }

  get stepTitle(): string {
    const titles = { 1: 'Report type', 2: 'Report details', 3: 'Optional filters' };
    return titles[this.step] || '';
  }

  get step2Valid(): boolean {
    return !!(
      this.form.country && this.form.maker && this.form.month && this.form.year && this.form.language &&
      (!this.isCampaignReport || this.form.campaignTag)
    );
  }

  get canGoBack(): boolean {
    return this.step > 1;
  }

  get showNext(): boolean {
    return this.step < this.stepTotal;
  }

  get showGenerate(): boolean {
    return this.step === this.stepTotal;
  }

  get nextEnabled(): boolean {
    if (this.step === 1) {
 return !!this.form.type;
}
    if (this.step === 2) {
 return this.step2Valid;
}
    return true;
  }

  get startPeriodValid(): boolean {
    const hasMonth = !!this.form.startMonth;
    const hasYear = !!this.form.startYear;
    return hasMonth === hasYear;
  }

  get generateEnabled(): boolean {
    return this.step2Valid && this.startPeriodValid;
  }

  get activeAxisOptions(): string[] {
    switch (this.form.axis) {
      case 'category': return this.categoryOptions;
      case 'subcategory': return this.subcategoryOptions;
      case 'segment': return this.segmentOptions;
      default: return [];
    }
  }

  get activeAxisLoading(): boolean {
    switch (this.form.axis) {
      case 'category': return this.loadingCategories;
      case 'subcategory': return this.loadingSubcategories;
      case 'segment': return this.loadingSegments;
      default: return false;
    }
  }

  get activeAxisPlaceholder(): string {
    switch (this.form.axis) {
      case 'category': return 'Select a category…';
      case 'subcategory': return 'Select a subcategory…';
      case 'segment': return 'Select a segment…';
      default: return 'Select…';
    }
  }

  onAxisChange(key: SegmentationAxis): void {
    if (this.form.axis === key) {
      this.form.axis = '';
      this.form.axisCategory = '';
      this.form.axisValue = '';
      return;
    }
    this.form.axis = key;
    this.form.axisCategory = '';
    this.form.axisValue = '';
    if (key === 'segment') {
      this._loadSegments();
    }
  }

  onAxisCategoryChange(name: string): void {
    this.form.axisCategory = name;
    this.form.axisValue = '';
    const entity = this._categoryEntities.find(e => e.name === name);
    this._loadSegments(entity ? Number(entity.id) : undefined);
  }

  onRetailerChange(entity: Entity): void {
    this.selectedRetailer = entity ?? null;
    this.form.retailer = entity?.name ?? '';
  }

  clearRetailer(): void {
    this.selectedRetailer = null;
    this.form.retailer = '';
    this._newRetailersDataSource();
  }

  onMakerChange(entity: Entity): void {
    this.selectedMaker = entity ?? null;
    this.form.maker = entity?.name ?? '';
    this.form.trademark = '';
    this.form.axisCategory = '';
    this.form.axisValue = '';
    this.trademarkOptions = [];
    this._trademarkEntities = [];
    this.categoryOptions = [];
    this.subcategoryOptions = [];
    this.segmentOptions = [];
    this._categoryEntities = [];
    this._subcategoryEntities = [];
    this._segmentEntities = [];

    if (entity) {
      const makerId = Number(entity.id);
      this._loadTrademarks(makerId);
      this._loadCategories(makerId);
      this._loadSubcategories(makerId);
    }
  }

  back(): void {
    if (this.step > 1) {
      if (this.step === 3) {
 this._newMakersDataSource();
}
      this.step--;
    }
  }

  next(): void {
    if (!this.nextEnabled) {
 return;
}
    const nextStep = Math.min(this.step + 1, this.stepTotal);
    if (nextStep === 2 && this.step !== 2) {
 this._newMakersDataSource();
}
    if (nextStep === 3 && this.step !== 3) {
 this._newRetailersDataSource();
}
    this.step = nextStep;
  }

  submit(): void {
    if (!this.generateEnabled || this.submitting) {
 return;
}
    const body: CreateReportRequestBody = {
      report_type_code: this.selectedType?.code ?? '',
      user_email: localStorage.getItem(STORAGE_KEY.email) || '',
      input_fields: this._buildInputFields()
    };
    this.submitting = true;
    this._raService.createReportRequest(body)
      .pipe(finalize(() => {
 this.submitting = false;
}))
      .subscribe({
        next: (res) => {
          const message = res?.reused
            ? 'A report with these exact parameters already exists. Showing the existing one.'
            : 'Report requested successfully';
          const type = res?.reused ? AlertsType.warning : AlertsType.success;
          this._alerts.openAlerts(message, type);
          this.dialogRef.close(true);
        },
        error: (err) => {
          const msg = err?.error?.message || 'Error creating report. Please try again.';
          this._alerts.openAlerts(msg, AlertsType.error);
        }
      });
  }

  close(): void {
    this.dialogRef.close(null);
  }

  private _buildInputFields(): CreateReportRequestBody['input_fields'] {
    const base = {
      maker_reference_code: String(this.selectedMaker?.reference_code ?? this.selectedMaker?.id ?? ''),
      maker_name: this.selectedMaker?.name ?? '',
      country: this._countryCode,
      country_name: this.form.country,
      pay_year: parseInt(this.form.year, 10),
      pay_month: MONTHS.indexOf(this.form.month) + 1,
      language: LANGUAGE_CODE[this.form.language] ?? this.form.language
    };

    if (this.isCampaignReport) {
      return { ...base, campaign_tag: this.form.campaignTag };
    }

    return {
      ...base,
      ...(this.form.trademark && { trademark: this.form.trademark }),
      ...(this.form.retailer && { retailer: this.form.retailer }),
      ...(this.form.vertical && { vertical: this.form.vertical }),
      ...(this.form.startYear && { start_year: parseInt(this.form.startYear, 10) }),
      ...(this.form.startMonth && { start_month: MONTHS.indexOf(this.form.startMonth) + 1 }),
      ...this._buildAxisField()
    };
  }

  private _buildAxisField(): Partial<Record<'category' | 'subcategory' | 'segment', string>> {
    if (!this.form.axis || !this.form.axisValue) {
 return {};
}
    return { [this.form.axis]: this.form.axisValue };
  }

  private _newMakersDataSource(): void {
    this.makersDataSource = new LazyScrollableDataSource<Entity>(this._fetchMakers.bind(this), 10, 1);
  }

  private _newRetailersDataSource(): void {
    this.retailersDataSource = new LazyScrollableDataSource<Entity>(this._fetchRetailers.bind(this), 10, 1);
  }

  private _fetchMakers(page: number, limit: number, search: string): Observable<Entity[]> {
    const params: FilterParams = { page, limit, search_criteria: SearchCriteria.name, search_value: search || '' };
    const body: FilterRequest = { entity: MasterDataEntity.maker, module: 'permissions' };
    return this._filtersService.getEntities(body, params)
      .pipe(
        map(({ data: { records } }: Permission<Paginate<Entity>>) => records || []),
        catchError(() => of([]))
      );
  }

  private _fetchRetailers(page: number, limit: number, search: string): Observable<Entity[]> {
    return this._raService.getStoreBrands(page, limit, search);
  }

  private _resolveAxisValueId(): string | number | null {
    if (!this.form.axis || !this.form.axisValue) {
 return null;
}
    const entitiesByAxis: Record<SegmentationAxis, Entity[]> = {
      category: this._categoryEntities,
      subcategory: this._subcategoryEntities,
      segment: this._segmentEntities,
      '': []
    };
    return entitiesByAxis[this.form.axis]?.find(e => e.name === this.form.axisValue)?.id ?? null;
  }

  private _loadReportTypes(): void {
    this.loadingReportTypes = true;
    this._raService.getReportTypes()
      .pipe(takeUntil(this.DESTROY_SUBJECT$), finalize(() => {
 this.loadingReportTypes = false;
}))
      .subscribe(types => {
 this.reportTypes = types.filter(t => t.code !== 'rpc_base_report');
});
  }

  private _loadTrademarks(makerId: number): void {
    this.loadingTrademarks = true;
    this._raService.getTrademarks(makerId)
      .pipe(takeUntil(this.DESTROY_SUBJECT$), finalize(() => {
 this.loadingTrademarks = false;
}))
      .subscribe((entities) => {
        this._trademarkEntities = entities;
        this.trademarkOptions = entities.map(e => e.name);
      });
  }

  private _loadVerticals(): void {
    this.loadingVerticals = true;
    this._raService.getVerticals()
      .pipe(takeUntil(this.DESTROY_SUBJECT$), finalize(() => {
 this.loadingVerticals = false;
}))
      .subscribe((entities) => {
        this._verticalEntities = entities;
        this.verticalOptions = entities.map(e => e.name);
      });
  }

  private _loadCategories(makerId?: number): void {
    this.loadingCategories = true;
    this._raService.getCategories(makerId)
      .pipe(takeUntil(this.DESTROY_SUBJECT$), finalize(() => {
 this.loadingCategories = false;
}))
      .subscribe((entities) => {
        this._categoryEntities = entities;
        this.categoryOptions = entities.map(e => e.name);
      });
  }

  private _loadSubcategories(makerId?: number): void {
    this.loadingSubcategories = true;
    this._raService.getSubcategories(makerId)
      .pipe(takeUntil(this.DESTROY_SUBJECT$), finalize(() => {
 this.loadingSubcategories = false;
}))
      .subscribe((entities) => {
        this._subcategoryEntities = entities;
        this.subcategoryOptions = entities.map(e => e.name);
      });
  }

  private _loadSegments(categoryId?: number): void {
    this.loadingSegments = true;
    this.segmentOptions = [];
    this._segmentEntities = [];
    this._raService.getSegments(categoryId)
      .pipe(takeUntil(this.DESTROY_SUBJECT$), finalize(() => {
 this.loadingSegments = false;
}))
      .subscribe((entities) => {
        this._segmentEntities = entities;
        this.segmentOptions = entities.map(e => e.name);
      });
  }
}
